<template>
  <div class="banner-slider-wrapper">
    <!-- Skeleton Loading -->
    <b-card
      v-if="isLoading"
      class="banner-skeleton border-0 shadow-sm rounded-lg overflow-hidden p-0 mb-0"
    >
      <div
        class="skeleton-img-placeholder d-flex align-items-center justify-content-center"
      >
        <b-spinner variant="primary" label="Loading..." />
      </div>
    </b-card>

    <!-- Empty State -->
    <b-card
      v-else-if="banners.length === 0"
      class="border-0 shadow-sm text-center py-4"
    >
      <p class="text-muted mb-0">Belum ada data banner terbaru.</p>
    </b-card>

    <!-- Swiper Slider -->
    <div v-else class="banner-slider-container position-relative">
      <swiper
        ref="bannerSwiper"
        class="banner-swiper"
        :options="swiperOptions"
        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
      >
        <swiper-slide
          v-for="(item, index) in banners"
          :key="`${item.type}-${item.id || index}`"
        >
          <div
            class="banner-card shadow-sm border-0 rounded-lg overflow-hidden bg-white"
            @click="goToPage(item)"
          >
            <div class="banner-section-container">
              <!-- Ambient Blurred Backdrop to fill left/right sides seamlessly -->
              <div class="banner-backdrop-wrapper">
                <img
                  :src="item.image || imageDummy"
                  :alt="item.title"
                  class="banner-backdrop-img"
                  aria-hidden="true"
                  @error="onImageError($event, item)"
                />
                <div class="banner-backdrop-overlay" />
              </div>

              <!-- Floating Badge: PCC (danger) or PRODUCT (primary) -->
              <div class="banner-floating-badge">
                <b-badge
                  :variant="item.type === 'pcc' ? 'danger' : 'primary'"
                  pill
                  class="banner-badge-pill"
                >
                  {{ item.type === 'pcc' ? 'PCC' : 'PRODUCT' }}
                </b-badge>
              </div>

              <!-- Main Banner Image in the Center (Uncropped, crisp, perfectly fitted) -->
              <div class="banner-main-image-wrap">
                <img
                  :src="item.image || imageDummy"
                  :alt="item.title"
                  class="banner-img"
                  :class="{ 'is-fallback-img': isFallbackImage(item) }"
                  @error="onImageError($event, item)"
                />
              </div>

              <!-- Tulisan di Tengah Foto jika Menggunakan Fallback Image -->
              <div
                v-if="isFallbackImage(item)"
                class="banner-fallback-center-text"
              >
                <div class="fallback-overlay-content text-center">
                  <div class="fallback-tag mb-1">
                    <feather-icon icon="PackageIcon" size="14" class="mr-50" />
                    <span>{{
                      item.type === 'pcc' ? 'PCC Event' : 'Product Catalog'
                    }}</span>
                  </div>
                  <h3
                    class="text-white font-weight-bolder mb-50 fallback-heading"
                  >
                    {{ item.title }}
                  </h3>
                  <p
                    class="text-white-50 mb-0 font-weight-semibold fallback-tagline"
                  >
                    Digital Connectivity
                  </p>
                </div>
              </div>
            </div>
          </div>
        </swiper-slide>

        <div slot="pagination" class="swiper-pagination" />
      </swiper>

      <!-- Navigation Buttons positioned on the card grid boundary -->
      <div
        v-show="banners.length > 1"
        class="swiper-button-prev banner-slider-btn banner-slider-btn-prev"
        role="button"
        tabindex="0"
        aria-label="Previous slide"
        @click="slidePrev"
      />
      <div
        v-show="banners.length > 1"
        class="swiper-button-next banner-slider-btn banner-slider-btn-next"
        role="button"
        tabindex="0"
        aria-label="Next slide"
        @click="slideNext"
      />
    </div>
  </div>
</template>

<script>
import { BCard, BBadge, BSpinner } from 'bootstrap-vue'
import { Swiper, SwiperSlide } from 'vue-awesome-swiper'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import defaultBannerBg from '@/assets/images/bg-07.png'
import 'swiper/css/swiper.css'

export default {
  name: 'ProductSliders',
  components: {
    BCard,
    BBadge,
    BSpinner,
    Swiper,
    SwiperSlide,
    FeatherIcon,
  },

  data() {
    return {
      isLoading: true,
      banners: [],
      imageDummy: defaultBannerBg,
    }
  },

  computed: {
    swiperOptions() {
      const hasMultiple = this.banners.length > 1
      return {
        slidesPerView: 1,
        spaceBetween: 20,
        loop: hasMultiple,
        autoplay: hasMultiple
          ? {
              delay: 5000,
              disableOnInteraction: false,
            }
          : false,
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
        },
        navigation: {
          nextEl: '.banner-slider-btn-next',
          prevEl: '.banner-slider-btn-prev',
        },
      }
    },
  },

  mounted() {
    this.fetchBanners()
  },

  methods: {
    stripHtml(value) {
      if (!value) return ''
      return String(value)
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },

    truncate(value, max = 160) {
      if (!value) return ''
      const cleaned = this.stripHtml(value)
      if (cleaned.length <= max) return cleaned
      return `${cleaned.substring(0, max).trim()}...`
    },

    isFallbackImage(item) {
      if (!item) return false
      return Boolean(
        item.isFallback || !item.image || item.image === this.imageDummy
      )
    },

    onImageError(event, item) {
      const imgEl = event ? event.target : null
      if (imgEl) {
        imgEl.src = this.imageDummy
      }
      if (item) {
        this.$set(item, 'isFallback', true)
      }
    },

    async fetchBanners() {
      this.isLoading = true

      const mapProductRequest = {
        map: {
          KODE: 's',
          PRODUCT: 's',
          PROD_DESCRIPTION: 'd',
          SHORT_DESCRIPTION: 'd',
          bannersLink: 'd',
          CREATED_DATE: 'd',
        },
      }

      let top5Products = []
      let activePCC = []

      // 1. Fetch 5 Top Item Produk (Logic Existing yang Dipertahankan)
      try {
        const productRes = await this.$http.post(
          '/product/browse/take/5/orderBy.CREATED_DATE/desc/case/banner/published/true',
          mapProductRequest
        )
        const productRecords = productRes?.data?.data?.records || []
        top5Products = productRecords
          .filter(
            item =>
              Array.isArray(item.bannersLink) &&
              item.bannersLink.length > 0 &&
              item.bannersLink[0].link
          )
          .map(item => {
            const dateVal =
              item.CREATED_DATE || item.created_at || item.LAST_UPDATE
            const hasBanner =
              Array.isArray(item.bannersLink) &&
              item.bannersLink.length > 0 &&
              Boolean(item.bannersLink[0].link)
            const bannerLink = hasBanner
              ? item.bannersLink[0].link
              : this.imageDummy

            return {
              type: 'product',
              id: item.KODE,
              badgeText: 'PRODUCT',
              title: item.PRODUCT || item.KODE,
              description: this.truncate(
                item.SHORT_DESCRIPTION || item.PROD_DESCRIPTION
              ),
              image: bannerLink,
              isFallback: !hasBanner,
              dateFormatted: dateVal
                ? this.$moment(dateVal).format('DD MMM YYYY')
                : '',
              route: {
                name: 'product-communication-detail',
                params: { key: item.KODE },
              },
            }
          })
      } catch (err) {
        console.error('[Banner] Gagal memuat produk:', err)
      }

      // 2. Fetch PCC & Filter SEMUA PCC yang Sedang Aktif Hari Ini
      try {
        const pccRes = await this.$http.get('/pcc', {
          params: {
            page: 1,
            perPage: 100,
            sort: '-created_at',
          },
        })
        const pccRecords = pccRes?.data?.data || []
        const today = this.$moment().format('YYYY-MM-DD')

        // PCC Aktif: Event yang belum berakhir (end_date >= today) dan masih open
        const isPccActivePeriod = item => {
          const endVal =
            item.end_date ||
            item.end_datetime ||
            item.start_date ||
            item.start_datetime
          if (!endVal) return false

          const end = this.$moment(endVal).format('YYYY-MM-DD')
          const isNotEnded = end >= today
          const isOpen = item.is_open !== false

          return isNotEnded && isOpen
        }

        activePCC = pccRecords
          .filter(item => {
            // PCC yang sudah lewat/tidak open cuma tampil kalau admin
            // mengaktifkan publish_banner_image_history (pakai Banner History).
            return (
              isPccActivePeriod(item) ||
              item.publish_banner_image_history === true
            )
          })
          .map(item => {
            const activePeriod = isPccActivePeriod(item)
            const bannerUrl = activePeriod
              ? item.banner_image_home_url || item.banner_image_url
              : item.banner_image_history_url ||
                item.banner_image_home_url ||
                item.banner_image_url
            const startVal = item.start_date || item.start_datetime
            const endVal = item.end_date || item.end_datetime

            // Speaker
            const primarySpeaker =
              Array.isArray(item.speakers) && item.speakers.length > 0
                ? item.speakers[0]
                : null
            const speakerName =
              primarySpeaker?.speaker || primarySpeaker?.name || ''
            const speakerPosition = primarySpeaker?.position || ''

            // Jadwal & Waktu
            const dateFull = startVal
              ? this.$moment(startVal).format('ddd, D MMMM YYYY')
              : ''
            let timeRange = ''
            if (startVal && endVal) {
              timeRange = `${this.$moment(startVal).format(
                'H:mm'
              )} to ${this.$moment(endVal).format('H:mm')}`
            } else if (startVal) {
              timeRange = this.$moment(startVal).format('H:mm')
            }

            // Lokasi
            const address = this.stripHtml(item.address || '')

            return {
              type: 'pcc',
              id: item.id,
              badgeText: 'PCC',
              title: item.title,
              description: this.truncate(item.description || item.address),
              speakerName,
              speakerPosition,
              dateFull,
              timeRange,
              address,
              image: bannerUrl || this.imageDummy,
              isFallback: !bannerUrl,
              dateFormatted: startVal
                ? this.$moment(startVal).format('DD MMM YYYY')
                : '',
              route: {
                name: 'detail-pcc',
                params: { id: item.id },
              },
            }
          })
      } catch (err) {
        console.error('[Banner] Gagal memuat PCC:', err)
      }

      // 3. Struktur Akhir: Semua PCC aktif + 5 Top Item Product
      this.banners = [...activePCC, ...top5Products]
      this.isLoading = false
    },

    goToPage(item) {
      if (item && item.route) {
        this.$router.push(item.route)
      }
    },

    slidePrev() {
      const swiper = this.$refs.bannerSwiper?.$swiper
      if (swiper) {
        swiper.slidePrev()
      }
    },

    slideNext() {
      const swiper = this.$refs.bannerSwiper?.$swiper
      if (swiper) {
        swiper.slideNext()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.banner-slider-wrapper {
  position: relative;
  width: 100%;
  margin-bottom: 1rem;
}

.banner-swiper {
  position: relative;
  padding-bottom: 2.2rem !important; /* Space for pagination bullets */

  ::v-deep .swiper-slide {
    height: auto;
    display: flex;
  }
}

.banner-card {
  width: 100%;
  // Matches the recommended banner upload size (1200 x 350 px, ~3.43:1) so a
  // correctly-sized banner fills the card edge-to-edge with no letterboxing.
  // Capped at 550px so the slider doesn't get unreasonably tall on very wide
  // monitors (falls back to the ambient blurred backdrop for the leftover space).
  aspect-ratio: 1200 / 350;
  height: auto;
  max-height: 550px;
  background: #080e1a;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 6px 24px 0 rgba(11, 23, 44, 0.14);
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  cursor: pointer;

  &:hover {
    box-shadow: 0 10px 30px 0 rgba(11, 23, 44, 0.2);
  }

  @media (max-width: 767.98px) {
    // Below ~768px, 1200:350 makes the banner too short to read comfortably,
    // so mobile keeps a fixed, taller height like before.
    aspect-ratio: unset;
    height: 250px;
    min-height: 250px;
  }
}

.banner-section-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #080e1a;
}

/* Ambient Blurred Backdrop to fill left/right sides seamlessly */
.banner-backdrop-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.banner-backdrop-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  filter: blur(35px) brightness(0.65);
  transform: scale(1.25);
  opacity: 0.85;
}

.banner-backdrop-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
    ellipse at center,
    rgba(11, 23, 44, 0.2) 0%,
    rgba(8, 14, 26, 0.82) 100%
  );
  background-image: radial-gradient(
    rgba(255, 255, 255, 0.08) 1px,
    transparent 1px
  );
  background-size: 20px 20px;
}

/* Main Banner Image in Center */
.banner-main-image-wrap {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.banner-img {
  // The card is now sized to the recommended banner ratio (1200 x 350), so the
  // banner image can fill it edge-to-edge instead of being letterboxed with the
  // blurred backdrop peeking through on the sides. `cover` crops any slight
  // mismatch instead of leaving gaps.
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  object-position: center !important;
  display: block !important;
  margin: 0 auto !important;
  border-radius: 12px !important;
  filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.45));
  transition: transform 0.35s ease;

  &:hover {
    transform: scale(1.015);
  }
}

.banner-floating-badge {
  position: absolute;
  top: 18px;
  left: 20px;
  z-index: 2;

  .banner-badge-pill {
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.8px;
    padding: 0.45rem 1.1rem;
    text-transform: uppercase;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
    border: 1px solid rgba(255, 255, 255, 0.3);
  }

  @media (max-width: 767.98px) {
    top: 12px;
    left: 14px;

    .banner-badge-pill {
      font-size: 0.7rem;
      padding: 0.35rem 0.75rem;
    }
  }
}

.banner-fallback-center-text {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    180deg,
    rgba(11, 23, 44, 0.45) 0%,
    rgba(11, 23, 44, 0.75) 100%
  );
  z-index: 1;
  padding: 1.5rem;
}

.fallback-overlay-content {
  max-width: 88%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.fallback-tag {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(4px);
  color: #ffffff;
  padding: 0.35rem 0.85rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.fallback-heading {
  font-size: 1.5rem;
  line-height: 1.3;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.7);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fallback-tagline {
  font-size: 0.9rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
}

/* Skeleton loader */
.skeleton-img-placeholder {
  width: 100%;
  aspect-ratio: 1200 / 350;
  height: auto;
  max-height: 550px;
  background-color: #f1f2f4;

  @media (max-width: 767.98px) {
    aspect-ratio: unset;
    height: 250px;
  }
}

/* Swiper Controls Navigation & Pagination */
.banner-slider-btn {
  position: absolute !important;
  top: calc((100% - 2.2rem) / 2) !important;
  z-index: 10 !important;
  width: 42px !important;
  height: 42px !important;
  border-radius: 50% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  margin: 0 !important;
  cursor: pointer !important;
  outline: none !important;

  /* Normal: Blurred / translucent glassmorphic effect */
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(8px) !important;
  -webkit-backdrop-filter: blur(8px) !important;
  border: 1px solid rgba(255, 255, 255, 0.85) !important;
  color: #7367f0 !important;
  box-shadow: 0 4px 14px rgba(34, 41, 47, 0.12) !important;
  opacity: 0.82;
  transition: all 0.25s ease !important;

  &:after {
    font-size: 16px !important;
    font-weight: bold !important;
    transition: transform 0.2s ease !important;
  }

  @media (max-width: 767.98px) {
    display: none !important;
  }
}

.banner-slider-btn-prev {
  left: 0 !important;
  right: auto !important;
  transform: translate(-50%, -50%) !important;

  &:hover {
    opacity: 1 !important;
    background: #ffffff !important;
    color: #7367f0 !important;
    border-color: #7367f0 !important;
    box-shadow: 0 6px 20px rgba(115, 103, 240, 0.4) !important;
    transform: translate(-50%, -50%) scale(1.12) !important;
  }

  &:active {
    transform: translate(-50%, -50%) scale(0.96) !important;
  }
}

.banner-slider-btn-next {
  right: 0 !important;
  left: auto !important;
  transform: translate(50%, -50%) !important;

  &:hover {
    opacity: 1 !important;
    background: #ffffff !important;
    color: #7367f0 !important;
    border-color: #7367f0 !important;
    box-shadow: 0 6px 20px rgba(115, 103, 240, 0.4) !important;
    transform: translate(50%, -50%) scale(1.12) !important;
  }

  &:active {
    transform: translate(50%, -50%) scale(0.96) !important;
  }
}

::v-deep .swiper-pagination-bullet {
  width: 10px;
  height: 10px;
  transition: all 0.3s ease;
  opacity: 0.5;
}

::v-deep .swiper-pagination-bullet-active {
  width: 26px;
  border-radius: 6px;
  background: #7367f0;
  opacity: 1;
}
</style>
